// import { call, fork, put, takeEvery } from 'redux-saga/effects';
import { all, call, fork, put, takeEvery, takeLeading } from 'redux-saga/effects';
import moment from 'moment';
import * as types from './examDataActionType';
import * as actions from './examDataActions';
import * as services from './examDataService';
import SweetAlert from '../../../../components/sweetalert/SweetAlert';
import { downloadBase64File } from '../../../../utils/fileDownloads';
import { buildEpplusWorkbook } from '../../../../utils/epplusXlsxWriter';
import { EXPORT_MODULE } from '../../../../constants/fileExport';
import { EXAM_DATA_EXPORT_SHEETS } from './examDataExportColumns';

//------------------ Exam Data
function* fetchExamDataSaga(action) {
    try {
        const response = yield call(services.getAllExamData, action.payload);

        if (!response) {
            throw new Error('No response received from API');
        }
        yield put(actions.fetchExamDataSuccess(response?.values, action.meta));

    } catch (error) {
        let errorMessage = 'Failed to fetch exam data.';
        console.error('Exam Data API Error:', {
            status: error.response?.status,
            message: errorMessage,
            data: error.response?.data,
            timestamp: new Date().toISOString()
        });
        yield put(actions.fetchExamDataFailure(errorMessage, action.meta));
    }
}

//------------------ Exam Data Dropdown Options
function* fetchExamDropdownOptionsSaga(action) {
    try {
        const response = yield call(services.getExamDropdownOptions, action.payload);

        yield put(actions.fetchExamDropdownOptionsSuccess(response?.values));
    } catch (error) {
        console.error("Exam Dropdown Options API Error:", error);
        yield put(actions.fetchExamDropdownOptionsFailure('Failed to fetch exam dropdown options.'));
    }
}

//------------------ Recalculate Data
function* fetchRecalculateDataSaga() {
    try {
        const response = yield call(services.getRecalculateData);

        yield put(actions.fetchRecalculateDataSuccess(response?.values));
    } catch (error) {
        console.error("Recalculate Data API Error:", error);
        yield put(actions.fetchRecalculateDataFailure('Failed to fetch recalculate data.'));
    }
}

//------------------ Exam Data Record Date
function* updateExamDataRecordDateSaga(action) {
    try {
        const response = yield call(services.updateExamDataRecordDate, action.payload);
        yield put(actions.updateExamDataRecordDateSuccess(response?.values));
    } catch (error) {
        console.error("Update Exam Data Record Date API Error:", error);
        yield put(actions.updateExamDataRecordDateFailure('Failed to update exam data record date.'));
    }
}

function* refreshRawExamDataSaga(action) {
    try {
        const response = yield call(services.refreshRawExamData, action.payload);
        if (!response) {
            throw new Error('No response received from API');
        }

        yield put(actions.refreshRawExamDataSuccess(response?.values));
    } catch (error) {
        let errorMessage = 'Failed to refresh raw exam data.';
        console.error('Refresh Raw Exam Data API Error:', {
            status: error.response?.status,
            message: errorMessage,
            data: error.response?.data,
            timestamp: new Date().toISOString()
        });
        yield put(actions.refreshRawExamDataFailure(errorMessage));
    }
}

function* saveBatchRecordExamDataSaga(action) {
    try {
        const response = yield call(
            services.getBatchRecordExamData,
            action.payload.ID
        );

        yield put(
            actions.saveBatchRecordExamDataSuccess(response?.values)
        );
    } catch (error) {
        console.error("Save Batch Record Exam Data API Error:", error);
        yield put(
            actions.saveBatchRecordExamDataFailure(
                'Failed to save batch record exam data.'
            )
        );
    }
}

//Get batch by record date
function* fetchExamDataBatchSaga(action) {
    try {
        const response = yield call(services.getExamDataBatchByRecordDate, action.payload.recordDate);
        yield put(actions.fetchExamDataBatchSuccess(response?.values));
    } catch (error) {
        console.error("Fetch Exam Data Batch API Error:", error);
        yield put(actions.fetchExamDataBatchFailure('Failed to fetch exam data batch.'));
    }
}

//Save batch record
function* saveBatchRecordSaga(action) {
    try {
        const response = yield call(services.saveBatchRecord, action.payload);
        yield put(actions.saveBatchRecordSuccess(response?.values));

        if (response && response.success) {
            SweetAlert.success({
                title: 'Success!',
                text: response?.resultMessage || 'Status updated successfully.'
            });
        } else {
            SweetAlert.error({
                title: 'Error!',
                text: response?.errorMessage || 'Failed to update status.'
            });
        }
    } catch (error) {
        yield put(actions.saveBatchRecordFailure('Failed to save batch record.'));
        SweetAlert.error({
            title: 'Error!',
            text: 'Failed to save batch record'
        });
    }
}

// Check Recalculation
function* checkRecalculationSaga() {
    try {
        const response = yield call(services.checkRecalculation);
        yield put(actions.checkRecalculationSuccess(response?.values));

        if (response && response.success) {
            SweetAlert.success({
                title: 'Success!',
                text: response?.resultMessage || 'updated successfully.'
            });
        } else {
            SweetAlert.error({
                title: 'Error!',
                text: response?.errorMessage || 'Failed to update.'
            });
        }
    } catch (error) {
        console.error("Check Recalculation API Error:", error);
        yield put(actions.checkRecalculationFailure('Failed to check recalculation.'));
        SweetAlert.error({
            title: 'Error!',
            text: 'Failed to check recalculation'
        });
    }
}

// Re Calculate Exam Data Details
function* reCalculateExamDataDetailsSaga(action) {
    try {
        const response = yield call(services.reCalculateExamDataDetails, action.payload);
        yield put(actions.reCalculateExamDataDetailsSuccess(response?.values));
    } catch (error) {
        console.error("Re Calculate Exam Data Details API Error:", error);
        yield put(actions.reCalculateExamDataDetailsFailure('Failed to re calculate exam data details.'));
    }
}

// Get Unassigned Combinations
function* fetchUnassignedCombinationsSaga(action) {
    try {
        const response = yield call(services.getUnassignedCombinations, action.payload);
        // Backend casing for the wrapped payload varies by endpoint (no camelCase
        // contract resolver is configured), so check every variant like the more
        // defensive sagas elsewhere in this codebase (e.g. configurationSaga.js, radSaga.js).
        const payload = response?.value || response?.Value || response?.values || response?.Values || {};
        yield put(actions.fetchUnassignedCombinationsSuccess(payload));
    } catch (error) {
        console.error("Unassigned Combinations API Error:", error);
        yield put(actions.fetchUnassignedCombinationsFailure('Failed to fetch unassigned combinations.'));
    }
}

// Check Worklist Assignments
function* checkWorklistAssignmentsSaga(action) {
    try {
        const response = yield call(services.checkWorklistAssignments, action.payload);
        const payload = response?.value || response?.Value || response?.values || response?.Values || {};

        yield put(actions.checkWorklistAssignmentsSuccess(payload));

        if (response && response.success) {
            // SweetAlert.success({
            //     title: 'Success!',
            //     text: response?.resultMessage || 'Successfully checked worklist assignments.'
            // });
        } else {
            // SweetAlert.error({
            //     title: 'Error!',
            //     text: response?.errorMessage || 'Failed to check worklist assignments.'
            // });
        }
    } catch (error) {
        console.error('Check Worklist Assignments API Error:', error);
        yield put(actions.checkWorklistAssignmentsFailure('Failed to check worklist assignments.'));
        // SweetAlert.error({
        //     title: 'Error!',
        //     text: 'Failed to check worklist assignments.'
        // });
    }
}

// Save Worklist Mapping
function* saveWorklistMappingSaga(action) {
    try {
        const response = yield call(services.saveWorklistMapping, action.payload);
        const payload = response?.value || response?.Value || response?.values || response?.Values || {};

        yield put(actions.saveWorklistMappingSuccess(payload));

        if (!(response && response.success)) {
            SweetAlert.error({
                title: 'Error!',
                text: response?.errorMessage || 'Failed to save worklist mapping.'
            });
        }
    } catch (error) {
        console.error('Save Worklist Mapping API Error:', error);
        yield put(actions.saveWorklistMappingFailure('Failed to save worklist mapping.'));
        SweetAlert.error({
            title: 'Error!',
            text: 'Failed to save worklist mapping.'
        });
    }
}

function* saveWorklistMappingUpdateSaga(action) {
    try {
        const response = yield call(services.saveWorklistMappingUpdate, action.payload);
        const payload = response?.value || response?.Value || response?.values || response?.Values || {};

        yield put(actions.saveWorklistMappingUpdateSuccess(payload));

        if (response && response.success) {
            SweetAlert.success({
                title: 'Success!',
                text: response?.resultMessage || 'Successfully saved worklist mapping.',
                customClass: { container: 'exam-data-swal-container' }
            });
        } else {
            SweetAlert.error({
                title: 'Error!',
                text: response?.errorMessage || 'Failed to save worklist mapping.',
                customClass: { container: 'exam-data-swal-container' }
            });
        }
    } catch (error) {
        console.error('Save Worklist Mapping Update API Error:', error);
        yield put(actions.saveWorklistMappingUpdateFailure('Failed to save worklist mapping update.'));
        SweetAlert.error({
            title: 'Error!',
            text: 'Failed to save worklist mapping update.',
            customClass: { container: 'exam-data-swal-container' }
        });
    }
}



// Get Unassigned Exam Data Count (tab badge, independent of the currently active tab)
function* fetchUnassignedExamDataCountSaga(action) {
    try {
        const response = yield call(services.getAllExamData, action.payload);
        yield put(actions.fetchUnassignedExamDataCountSuccess(response?.values?.recordsTotal || 0));
    } catch (error) {
        console.error("Unassigned Exam Data Count API Error:", error);
        yield put(actions.fetchUnassignedExamDataCountFailure('Failed to fetch unassigned exam data count.'));
    }
}

// Bypasses the shared exportToCSVRequest/commonSaga (Base64-in-JSON) flow: this export can return
// very large workbooks, so it downloads a binary blob directly to avoid the size/memory overhead of
// Base64-encoding + JSON-wrapping the file (see examDataService.js).
// function* exportCalculatedExamDataToCSVSaga(action) {
//     try {
//         const { fromDate, toDate, fileName } = action.payload || {};
//         const response = yield call(services.exportCalculatedExamDataToCSV, fromDate, toDate);
//
//         const contentType = response.headers?.['content-type'] || '';
//         if (contentType.includes('application/json')) {
//             const errorText = yield response.data.text();
//             const errorBody = JSON.parse(errorText);
//             throw new Error(errorBody?.Message || errorBody?.message);
//         }
//
//         yield call(downloadBase64File, response.data, EXPORT_MODULE.EXAM_DATA, fileName);
//         yield put(actions.exportCalculatedExamDataToCSVSuccess());
//         SweetAlert.success({
//             title: 'Success!',
//             text: 'Successfully exported data.'
//         });
//     } catch (error) {
//         console.error('Export Calculated Exam Data API Error:', error);
//         yield put(actions.exportCalculatedExamDataToCSVFailure(error?.message || 'Failed to export data.'));
//         SweetAlert.error({
//             title: 'Error!',
//             text: error?.message || 'Failed to export data.'
//         });
//     }
// }

// Large date ranges took longer than Azure App Service's 230-second request limit when the API
// built the whole workbook in one request. Instead, the data is fetched in day-sized slices (a few
// at a time) through GetAllCalculatedExamData, merged exactly as sp_ExamData_Details_GetAll would
// return it for the whole range, and the workbook is built in the browser in the same format the
// API produced (see utils/epplusXlsxWriter.js and examDataExportColumns.js).
const EXAM_DATA_EXPORT_DATE_FORMAT = 'MM.DD.YYYY h A';
const EXPORT_SLICE_CONCURRENCY = 3;
const EXPORT_SLICE_ATTEMPTS = 2;

// Each slice runs from its start to 12 AM of the next day. The API includes the whole ToDate hour,
// so consecutive slices overlap by one hour and no record at a day boundary can fall between them;
// the overlap is removed again by ExamDataDetailID in mergeExportSlices.
const getExportRanges = (fromDate, toDate) => {
    const start = moment(fromDate, EXAM_DATA_EXPORT_DATE_FORMAT, true);
    const end = moment(toDate, EXAM_DATA_EXPORT_DATE_FORMAT, true);
    if (!start.isValid() || !end.isValid()) {
        throw new Error('Failed to export data.');
    }
    if (!start.isBefore(end)) {
        return [{ fromDate, toDate }];
    }

    const ranges = [];
    let cursor = start.clone();
    let sliceEnd;
    do {
        const nextDay = cursor.clone().startOf('day').add(1, 'day');
        sliceEnd = moment.min(nextDay, end);
        ranges.push({
            fromDate: cursor.format(EXAM_DATA_EXPORT_DATE_FORMAT),
            toDate: sliceEnd.format(EXAM_DATA_EXPORT_DATE_FORMAT),
        });
        cursor = nextDay;
    } while (sliceEnd.isBefore(end));
    return ranges;
};

const toExportMergeRow = (sheet, record) => ({
    id: record.ExamDataDetailID,
    accession: record.Accession ?? '-',
    unreadDate: record.UnReadDate || '',
    unreadHours: record.UnReadHours === null || record.UnReadHours === undefined ? '' : String(record.UnReadHours),
    cells: sheet.toRow(record),
});

// Returns, per export sheet, the rows of one slice.
function* fetchExamDataExportSlice({ fromDate, toDate }) {
    for (let attempt = 1; ; attempt++) {
        try {
            const response = yield call(services.getCalculatedExamDataByRange, fromDate, toDate);
            const values = response?.values;
            if (!values) {
                throw new Error('No response received from API');
            }
            return EXAM_DATA_EXPORT_SHEETS.map((sheet) =>
                (values[sheet.listKey] || []).map((record) => toExportMergeRow(sheet, record)),
            );
        } catch (error) {
            if (attempt >= EXPORT_SLICE_ATTEMPTS) {
                console.error(`Export Calculated Exam Data API Error (${fromDate} - ${toDate}):`, error);
                throw new Error(`Failed to export data for ${fromDate} - ${toDate}.`);
            }
        }
    }
}

// Rebuilds what sp_ExamData_Details_GetAll returns for the whole range from the slices: rows
// fetched twice in an overlap hour are kept once, each Accession keeps only its lowest
// ExamDataDetailID (its ROW_NUMBER() ... = 1 filter), and rows are ordered by UnReadDate then
// UnReadHours (an NVARCHAR column, so compared as text).
const mergeExportSlices = (slices) => {
    const byId = new Map();
    slices.forEach((rows) => rows.forEach((row) => {
        if (!byId.has(row.id)) byId.set(row.id, row);
    }));

    const firstByAccession = new Map();
    byId.forEach((row) => {
        const current = firstByAccession.get(row.accession);
        if (!current || row.id < current.id) firstByAccession.set(row.accession, row);
    });

    const compareText = (a, b) => (a < b ? -1 : a > b ? 1 : 0);
    return [...byId.values()]
        .filter((row) => firstByAccession.get(row.accession) === row)
        .sort((a, b) => compareText(a.unreadDate, b.unreadDate) || compareText(a.unreadHours, b.unreadHours))
        .map((row) => row.cells);
};

function* exportCalculatedExamDataToCSVSaga(action) {
    try {
        const { fromDate, toDate, fileName } = action.payload || {};
        const ranges = getExportRanges(fromDate, toDate);

        const slicesBySheet = EXAM_DATA_EXPORT_SHEETS.map(() => []);
        for (let i = 0; i < ranges.length; i += EXPORT_SLICE_CONCURRENCY) {
            const batch = ranges.slice(i, i + EXPORT_SLICE_CONCURRENCY);
            const batchSlices = yield all(batch.map((range) => call(fetchExamDataExportSlice, range)));
            batchSlices.forEach((sheetRows) =>
                sheetRows.forEach((rows, sheetIndex) => slicesBySheet[sheetIndex].push(rows)),
            );
        }

        const workbook = yield call(
            buildEpplusWorkbook,
            EXAM_DATA_EXPORT_SHEETS.map((sheet, sheetIndex) => ({
                name: sheet.name,
                headers: sheet.headers,
                columnTypes: sheet.columnTypes,
                formatNumbers: true,
                rows: mergeExportSlices(slicesBySheet[sheetIndex]),
            })),
        );

        yield call(downloadBase64File, workbook, EXPORT_MODULE.EXAM_DATA, fileName);
        yield put(actions.exportCalculatedExamDataToCSVSuccess());
        SweetAlert.success({
            title: 'Success!',
            text: 'Successfully exported data.'
        });
    } catch (error) {
        console.error('Export Calculated Exam Data API Error:', error);
        yield put(actions.exportCalculatedExamDataToCSVFailure(error?.message || 'Failed to export data.'));
        SweetAlert.error({
            title: 'Error!',
            text: error?.message || 'Failed to export data.'
        });
    }
}

export default function* watchMiscellaneousSaga() {
    yield takeEvery(types.FETCH_EXAM_DATA_REQUEST, fetchExamDataSaga);
    yield takeEvery(types.FETCH_EXAM_DROPDOWN_OPTIONS_REQUEST, fetchExamDropdownOptionsSaga);
    yield takeEvery(types.FETCH_RECALCULATE_DATA_REQUEST, fetchRecalculateDataSaga);
    yield takeEvery(types.UPDATE_EXAM_DATA_RECORD_DATE_REQUEST, updateExamDataRecordDateSaga);
    yield takeEvery(types.REFRESH_RAW_EXAM_DATA_REQUEST, refreshRawExamDataSaga);
    yield takeEvery(types.SAVE_BATCH_RECORD_EXAM_DATA_REQUEST, saveBatchRecordExamDataSaga);
    yield takeEvery(types.FETCH_EXAM_DATA_BATCH_REQUEST, fetchExamDataBatchSaga);
    yield takeEvery(types.SAVE_BATCH_RECORD_REQUEST, saveBatchRecordSaga);
    yield takeEvery(types.CHECK_RECALCULATION_REQUEST, checkRecalculationSaga);
    yield takeEvery(types.RE_CALCULATE_EXAM_DATA_DETAILS_REQUEST, reCalculateExamDataDetailsSaga);
    yield takeEvery(types.FETCH_UNASSIGNED_COMBINATIONS_REQUEST, fetchUnassignedCombinationsSaga);
    yield takeEvery(types.CHECK_WORKLIST_ASSIGNMENTS_REQUEST, checkWorklistAssignmentsSaga);
    yield takeEvery(types.SAVE_WORKLIST_MAPPING_REQUEST, saveWorklistMappingSaga);
    yield takeEvery(types.SAVE_WORKLIST_MAPPING_UPDATE_REQUEST, saveWorklistMappingUpdateSaga);
    yield takeEvery(types.FETCH_UNASSIGNED_EXAM_DATA_COUNT_REQUEST, fetchUnassignedExamDataCountSaga);
    // yield takeEvery(types.EXPORT_CALCULATED_EXAM_DATA_REQUEST, exportCalculatedExamDataToCSVSaga);
    // takeLeading: ignore further Export clicks while an export is still running.
    yield takeLeading(types.EXPORT_CALCULATED_EXAM_DATA_REQUEST, exportCalculatedExamDataToCSVSaga);
}